// Story 7.6 — minimal intersection-observer hook (no dependency; nothing
// existed in the repo). Fires `onIntersect` whenever the observed element
// enters the expanded viewport (rootMargin pushes the trigger line 200px
// below the fold for infinite-scroll prefetch). Falls back to a no-op
// when IntersectionObserver is unavailable (old jsdom) — tests stub the
// global instead.

import { useEffect, useRef } from "react";

export function useIntersectionObserver<T extends HTMLElement>(
  onIntersect: () => void,
  { rootMargin = "0px", enabled = true }: { rootMargin?: string; enabled?: boolean } = {},
) {
  const targetRef = useRef<T | null>(null);
  const callbackRef = useRef(onIntersect);
  callbackRef.current = onIntersect;

  useEffect(() => {
    const target = targetRef.current;
    if (!enabled || !target || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) callbackRef.current();
        }
      },
      { rootMargin },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [rootMargin, enabled]);

  return targetRef;
}
